import React from 'react';
import { Check, Shield, Zap, Settings, Star, TrendingUp, CreditCard, Send, Lock, Store, ArrowRight, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
const featureIcons = [Shield, Star, TrendingUp];
export function PricingSection() {
  const {
    t
  } = useLang();
  return <section id="servicios" className="py-24 bg-dark relative" data-id="element-349" data-mp="1jas2usjv0r22">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[100px] pointer-events-none" data-id="element-350" data-mp="1na0spmbs44sc"></div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-351" data-mp="1x86rhv2agusz">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-352" data-mp="227takl4st59p">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-353" data-mp="7vmtx6t4xwa0">
            {t.pricing.title} <br className="hidden md:block" data-id="element-354" data-mp="1mihmsuo141ug" />
            <span className="text-gold" data-id="element-355" data-mp="1hpofbz16efeo">{t.pricing.titleHighlight}</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-356" data-mp="1crwqjdvtv9ep">
          {/* Main Pricing Card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-gradient-to-br from-dark-100 to-dark-200 rounded-3xl border-2 border-gold p-8 md:p-10 shadow-[0_0_40px_rgba(212,175,55,0.15)] relative overflow-hidden" data-id="element-357" data-mp="17sto8178fhaf">
            <div className="absolute top-0 right-0 bg-gold text-dark font-bold px-6 py-2 rounded-bl-2xl text-sm uppercase tracking-wider z-10" data-id="element-358" data-mp="k03abmfrqnxi">
              {t.pricing.badge}
            </div>

            <div className="relative -mx-8 md:-mx-10 -mt-8 md:-mt-10 mb-8 rounded-t-3xl overflow-hidden" data-id="element-359" data-mp="hijbl7qkp0w0">
              <img src="https://cdn.magicpatterns.com/uploads/joXRLUuV19avwWiRtVmmjh/heylloyd-image-1782678468852.png" alt={t.pricing.imageAlt} className="w-full h-64 md:h-72 object-cover object-[center_38%]" data-id="element-360" data-mp="meryj7b0hydq" />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-100 via-dark-100/20 to-transparent" data-id="element-361" data-mp="3uvwb0zsjrdbc"></div>
            </div>

            <div className="mb-8" data-id="element-362" data-mp="3jtis4asdndg">
              <h3 className="flex items-center gap-2 text-xl font-bold text-white mb-4" data-id="element-363" data-mp="3kz9o85gnuc6o">
                <Zap className="w-5 h-5 text-gold" data-id="element-364" data-mp="3px1r1uiufipn" />
                {t.pricing.cardTitle}
              </h3>
              <div className="flex items-baseline mb-3" data-id="element-365" data-mp="3bxjx70qoxf9f">
                <span className="text-6xl font-extrabold text-gold" data-id="element-366" data-mp="3c064cz9o5sr4">
                  {t.pricing.price}
                </span>
                <span className="text-gray-400 ml-2 font-medium" data-id="element-367" data-mp="2xal8ven4r0yx">
                  {t.pricing.priceUnit}
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-368" data-mp="2sdl5xclkuec1">
                {t.pricing.noteA}{' '}
                <strong className="text-white" data-id="element-369" data-mp="36d2t5nj85jja">{t.pricing.noteRest}</strong>{' '}
                {t.pricing.noteB}{' '}
                <strong className="text-white" data-id="element-370" data-mp="31f1ah3ppnm8c">{t.pricing.noteBold}</strong>
                {t.pricing.noteC}
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-371" data-mp="3b9td4ly5gz9z">
              {t.pricing.features.map((text, i) => <div key={i} className="flex items-center gap-3" data-id="element-372" data-mp="3p17uypgbmufg" data-mpi={i}>
                  <div className="bg-gold/20 p-1 rounded-full shrink-0" data-id="element-373" data-mp="3l0xqxic3ae38">
                    <Check className="w-4 h-4 text-gold" data-id="element-374" data-mp="3l31rbqas4uyr" />
                  </div>
                  <span className="text-white font-medium" data-id="element-375" data-mp="2vccq0ed3tdh">{text}</span>
                </div>)}
            </div>

            <a href="https://wa.me/447311754270?text=DEMO" target="_blank" rel="noopener noreferrer" className="block w-full py-4 text-center bg-gold hover:bg-gold-400 text-dark font-bold rounded-xl transition-colors" data-id="element-376" data-mp="hy2ml11ty51b">
              {t.pricing.cta}
            </a>

            {/* Payment methods */}
            <div className="mt-6 pt-6 border-t border-white/10" data-id="element-377" data-mp="vopkws3ohswv">
              <div className="flex items-center justify-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-378" data-mp="3v5nvzx2shybo">
                <Lock className="w-3.5 h-3.5 text-gold" data-id="element-379" data-mp="313hpkyd4qaj" />
                {t.pricing.paymentTitle}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-380" data-mp="829blm3rejln">
                <div className="flex items-center gap-2 bg-dark-300/60 border border-white/10 rounded-xl px-4 py-2.5" data-id="element-381" data-mp="d0awmckqwszr">
                  <Send className="w-4 h-4 text-gold" data-id="element-382" data-mp="1jhfyf5r8ck99" />
                  <span className="text-white font-semibold text-sm" data-id="element-383" data-mp="1ocv5neuqfhwp">
                    {t.pricing.zelle}
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-dark-300/60 border border-white/10 rounded-xl px-4 py-2.5" data-id="element-384" data-mp="33xdyghv8dtwb">
                  <CreditCard className="w-4 h-4 text-gold" data-id="element-385" data-mp="2z5m5y1sile3j" />
                  <span className="text-white font-semibold text-sm" data-id="element-386" data-mp="2z41c6ee0xz6q">
                    {t.pricing.card}
                  </span>
                  <span className="text-gray-500 text-xs" data-id="element-387" data-mp="2q68w380hania">
                    {t.pricing.viaStripe}
                  </span>
                </div>
              </div>

              <p className="mt-5 flex items-start justify-center gap-2 text-[11px] leading-relaxed text-gray-500 text-center" data-id="element-388" data-mp="2l8z6nsd3abug">
                <Globe className="w-3.5 h-3.5 text-gray-600 shrink-0 mt-px" data-id="element-389" data-mp="2b8yjtwg0hdli" />
                <span data-id="element-390" data-mp="eg6i9fole5i4">{t.pricing.domainNote}</span>
              </p>
            </div>
          </motion.div>

          {/* Features & Maintenance */}
          <div className="space-y-6" data-id="element-391" data-mp="9idf6vil5rm2">
            <div className="grid gap-4" data-id="element-392" data-mp="9f937byvprku">
              {t.pricing.featureCards.map((feat, i) => {
              const Icon = featureIcons[i];
              return <motion.div key={i} initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="bg-dark-100 p-5 rounded-2xl border border-white/5 flex items-center gap-4" data-id="element-393" data-mp="28plf84o11ad2" data-mpi={i}>
                    <Icon className="w-8 h-8 text-gold shrink-0" data-id="element-394" data-mp="2hmvt7xs8bdlo" />
                    <div data-id="element-395" data-mp="126vktresga8c">
                      <h4 className="text-white font-bold" data-id="element-396" data-mp="126n7xzio14x5">{feat.title}</h4>
                      <p className="text-gray-400 text-sm" data-id="element-397" data-mp="16al544ofk5nk">{feat.desc}</p>
                    </div>
                  </motion.div>;
            })}
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-dark-200 rounded-2xl border border-white/10 p-6 flex items-center justify-between" data-id="element-398" data-mp="trfugjym2tmn">
              <div data-id="element-399" data-mp="pp2opvjv6vpv">
                <div className="inline-flex items-center gap-2 text-gray-400 font-bold uppercase text-xs mb-1" data-id="element-400" data-mp="ppkt5fgokg8v">
                  <Settings className="w-4 h-4" data-id="element-401" data-mp="kxkxhz7n89i3" /> {t.pricing.optional}
                </div>
                <h4 className="text-white font-bold" data-id="element-402" data-mp="3tde3vl89g4lf">{t.pricing.maintenance}</h4>
                <p className="text-gray-400 text-sm" data-id="element-403" data-mp="3ofuq0mfh5tu0">
                  {t.pricing.maintenanceDesc}
                </p>
              </div>
              <div className="text-right" data-id="element-404" data-mp="32xkspgc2p2wg">
                <div className="text-2xl font-bold text-white" data-id="element-405" data-mp="37v373m8v0abu">$30</div>
                <div className="text-xs text-gray-500" data-id="element-406" data-mp="37x76przq6n6b">
                  {t.pricing.perMonth}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Other businesses note */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-10 bg-dark-100 border border-gold/20 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left" data-id="element-407" data-mp="3luvv39t04cg4">
          <div className="flex items-center gap-4" data-id="element-408" data-mp="8dwicn5swvks">
            <div className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gold/15 text-gold" data-id="element-409" data-mp="deannxg0prf4">
              <Store className="w-6 h-6" data-id="element-410" data-mp="1xyo8x8fxx11w" />
            </div>
            <div data-id="element-411" data-mp="1sxift9idbbru">
              <h4 className="text-white font-bold" data-id="element-412" data-mp="1swyv5l1c29a4">{t.pricing.otherTitle}</h4>
              <p className="text-gray-400 text-sm" data-id="element-413" data-mp="1ovwtyynuvn5i">{t.pricing.otherDesc}</p>
            </div>
          </div>
          <a href="https://wa.me/447311754270?text=Hola,%20me%20interesa%20un%20website%20para%20mi%20negocio" target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 py-3 px-6 bg-gold/10 hover:bg-gold hover:text-dark text-gold font-bold rounded-xl border border-gold/30 transition-colors" data-id="element-414" data-mp="1jy3pqb64537v">
            {t.pricing.otherCta}
            <ArrowRight className="w-4 h-4" data-id="element-415" data-mp="22venfrc3jol7" />
          </a>
        </motion.div>
      </div>
    </section>;
}